import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { PERSONAS } from "@/lib/simulator";

const NAVY = "#0B1F2A";
const GOLD = "#C89B2A";
const TEAL = "#1F6F6A";
const WHITE = "#FFFFFF";
const MUTED = "#94A3B8";

// Helper: snapshot a DOM node to dataURL
async function snapshot(node) {
    if (!node) return null;
    const canvas = await html2canvas(node, {
        backgroundColor: "#0F2A38",
        scale: 1.4,
        useCORS: true,
        logging: false
    });
    return canvas.toDataURL("image/jpeg", 0.85);
}

export async function exportSimulatorPDF({ personaKey, targets, result, regulatorTargets, riskChartRef, financialChartRef }) {
    const persona = PERSONAS[personaKey];
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const W = doc.internal.pageSize.getWidth();
    const H = doc.internal.pageSize.getHeight();
    const M = 40; // margin

    // Background
    doc.setFillColor(NAVY);
    doc.rect(0, 0, W, H, "F");

    // Header band
    doc.setDrawColor(GOLD);
    doc.setLineWidth(0.5);
    doc.line(M, 60, W - M, 60);

    // Eyebrow
    doc.setFont("courier", "normal");
    doc.setFontSize(8);
    doc.setTextColor(GOLD);
    doc.text("CARBONIS · CLIMATE RISK BRIEFING", M, 50);
    doc.setTextColor(MUTED);
    doc.text(new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" }).toUpperCase(), W - M, 50, { align: "right" });

    // Title
    doc.setFont("times", "normal");
    doc.setFontSize(28);
    doc.setTextColor(WHITE);
    doc.text(persona.label + " · Risk Simulation", M, 100);
    doc.setFontSize(10);
    doc.setTextColor(MUTED);
    doc.text(persona.subtitle, M, 120);

    // Persona baseline block
    const baseY = 155;
    doc.setDrawColor(255, 255, 255, 0.15);
    doc.setLineWidth(0.4);
    doc.rect(M, baseY, W - 2 * M, 90);

    doc.setFont("courier", "normal");
    doc.setFontSize(7);
    doc.setTextColor(GOLD);
    doc.text("PERSONA BASELINE · tCO₂e / yr", M + 10, baseY + 18);

    const b = persona.baseline;
    const kvs = [
        ["Scope 1", b.s1.toLocaleString("en-IN")],
        ["Scope 2", b.s2.toLocaleString("en-IN")],
        ["Scope 3", b.s3.toLocaleString("en-IN")],
        ["Total", (b.s1 + b.s2 + b.s3).toLocaleString("en-IN")]
    ];
    kvs.forEach(([k, v], i) => {
        const cx = M + 15 + i * ((W - 2 * M - 30) / 4);
        doc.setFont("courier", "normal");
        doc.setFontSize(7);
        doc.setTextColor(MUTED);
        doc.text(k.toUpperCase(), cx, baseY + 40);
        doc.setFont("times", "normal");
        doc.setFontSize(18);
        doc.setTextColor(WHITE);
        doc.text(v, cx, baseY + 65);
    });

    // Reduction targets block
    const tgtY = 265;
    doc.setFont("courier", "normal");
    doc.setFontSize(8);
    doc.setTextColor(GOLD);
    doc.text("REDUCTION TARGETS · BY 2030", M, tgtY);

    ["s1", "s2", "s3"].forEach((k, i) => {
        const cx = M + i * ((W - 2 * M) / 3);
        const bw = (W - 2 * M) / 3 - 10;
        doc.setDrawColor(255, 255, 255, 0.15);
        doc.rect(cx, tgtY + 10, bw, 70);
        doc.setFont("courier", "normal");
        doc.setFontSize(7);
        doc.setTextColor(MUTED);
        doc.text(`SCOPE ${i + 1}`, cx + 10, tgtY + 28);
        doc.setFont("times", "normal");
        doc.setFontSize(26);
        doc.setTextColor(GOLD);
        doc.text(`-${targets[k]}%`, cx + 10, tgtY + 60);

        if (personaKey !== "regulator" && regulatorTargets) {
            doc.setFont("courier", "normal");
            doc.setFontSize(6);
            doc.setTextColor(TEAL);
            doc.text(`REGULATOR: -${regulatorTargets[k]}%`, cx + 10, tgtY + 74);
        }
    });

    // KPIs
    const kpiY = 375;
    doc.setFont("courier", "normal");
    doc.setFontSize(8);
    doc.setTextColor(GOLD);
    doc.text("SIMULATED OUTCOMES", M, kpiY);

    const kpis = [
        { l: "Simulated Risk", v: `${result.simulatedScore}`, u: "/ 100" },
        { l: "Emissions Avoided", v: `${(result.totalBaseline - result.totalReduced).toLocaleString("en-IN")}`, u: "tCO₂e / yr" },
        { l: "Annual Savings", v: `₹${result.totalSavingsInrCr}`, u: "Cr / yr" },
        { l: "COC Uplift Avoided", v: `${result.cocBpsSavings}`, u: "bps" }
    ];
    const colW = (W - 2 * M) / 4;
    kpis.forEach((k, i) => {
        const cx = M + i * colW;
        doc.setDrawColor(255, 255, 255, 0.15);
        doc.rect(cx, kpiY + 10, colW - 5, 70);
        doc.setFont("courier", "normal");
        doc.setFontSize(6);
        doc.setTextColor(MUTED);
        doc.text(k.l.toUpperCase(), cx + 8, kpiY + 25);
        doc.setFont("times", "normal");
        doc.setFontSize(22);
        doc.setTextColor(WHITE);
        doc.text(k.v, cx + 8, kpiY + 55);
        doc.setFont("courier", "normal");
        doc.setFontSize(6);
        doc.setTextColor(MUTED);
        doc.text(k.u.toUpperCase(), cx + 8, kpiY + 72);
    });

    // Charts snapshots
    const chartY = 480;
    doc.setFont("courier", "normal");
    doc.setFontSize(8);
    doc.setTextColor(GOLD);
    doc.text("FIG. 01 · RISK TRAJECTORY", M, chartY);
    doc.text("FIG. 02 · FINANCIAL IMPACT (₹ CR / YR)", M + (W - 2 * M) / 2 + 10, chartY);

    try {
        const [riskImg, finImg] = await Promise.all([
            snapshot(riskChartRef?.current),
            snapshot(financialChartRef?.current)
        ]);
        const chartW = (W - 2 * M - 10) / 2;
        const chartH = 200;
        if (riskImg) doc.addImage(riskImg, "JPEG", M, chartY + 10, chartW, chartH);
        if (finImg) doc.addImage(finImg, "JPEG", M + chartW + 10, chartY + 10, chartW, chartH);
    } catch (e) {
        doc.setFontSize(8);
        doc.setTextColor(MUTED);
        doc.text("Chart snapshot unavailable.", M, chartY + 30);
    }

    // Footer
    doc.setDrawColor(GOLD);
    doc.line(M, H - 50, W - M, H - 50);
    doc.setFont("courier", "normal");
    doc.setFontSize(6);
    doc.setTextColor(MUTED);
    doc.text("CARBONIS · CLIMATE RISK INTELLIGENCE · ILLUSTRATIVE MODEL · NOT INVESTMENT ADVICE", M, H - 35);
    doc.text("info@carbonis.in · An UP/OR/OUT Company", W - M, H - 35, { align: "right" });

    const filename = `carbonis-${personaKey}-briefing-${new Date().toISOString().slice(0, 10)}.pdf`;
    doc.save(filename);
    return filename;
}
